/**
 * Pre-login state persistence utility
 * Saves UI state before login and restores it after login
 */

export interface PreLoginState {
  // Chat state
  chatState?: {
    conversationId: string | null;
    sessionId: string | null;
    activeTab?: string;
  };
  // Booking form state (already handled via bookingFormDraft in localStorage)
  bookingState?: {
    experienceType?: string;
    experienceSlug?: string;
  };
  // General UI state
  filters?: Record<string, unknown>;
  searchQuery?: string;
  timestamp: number;
}

const PRE_LOGIN_STATE_KEY = 'nexgo-pre-login-state';

/**
 * Save state before login redirect
 */
export function savePreLoginState(state: Partial<PreLoginState>): void {
  if (typeof window === 'undefined') return;

  try {
    const existingState = getPreLoginState();
    const newState: PreLoginState = {
      ...existingState,
      ...state,
      timestamp: Date.now(),
    };
    localStorage.setItem(PRE_LOGIN_STATE_KEY, JSON.stringify(newState));
    console.log('💾 [PreLoginState] State saved:', newState);
  } catch (error) {
    console.error('❌ [PreLoginState] Failed to save state:', error);
  }
}

/**
 * Get saved pre-login state
 */
export function getPreLoginState(): PreLoginState | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(PRE_LOGIN_STATE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as PreLoginState;
    // Clear state older than 1 hour
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    if (parsed.timestamp < oneHourAgo) {
      clearPreLoginState();
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('❌ [PreLoginState] Failed to read state:', error);
    return null;
  }
}

/**
 * Clear saved pre-login state
 */
export function clearPreLoginState(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(PRE_LOGIN_STATE_KEY);
    console.log('🗑️ [PreLoginState] State cleared');
  } catch (error) {
    console.error('❌ [PreLoginState] Failed to clear state:', error);
  }
}

/**
 * Debug utility to check pre-login state
 * Use this in browser console to diagnose issues
 */
export function debugPreLoginState(): {
  hasState: boolean;
  state: PreLoginState | null;
  rawStorage: string | null;
  timestamp: number | null;
  age: string | null;
  isValid: boolean;
} {
  if (typeof window === 'undefined') {
    return {
      hasState: false,
      state: null,
      rawStorage: null,
      timestamp: null,
      age: null,
      isValid: false,
    };
  }

  try {
    const raw = localStorage.getItem(PRE_LOGIN_STATE_KEY);
    if (!raw) {
      console.log('🔍 [PreLoginState Debug] No state found in localStorage');
      return {
        hasState: false,
        state: null,
        rawStorage: null,
        timestamp: null,
        age: null,
        isValid: false,
      };
    }

    const parsed = JSON.parse(raw) as PreLoginState;
    const age = parsed.timestamp
      ? `${Math.round((Date.now() - parsed.timestamp) / 1000)}s ago`
      : null;
    const isValid = parsed.timestamp
      ? Date.now() - parsed.timestamp < 60 * 60 * 1000
      : false;

    const debugInfo = {
      hasState: true,
      state: parsed,
      rawStorage: raw,
      timestamp: parsed.timestamp,
      age,
      isValid,
    };

    console.log('🔍 [PreLoginState Debug]', debugInfo);
    return debugInfo;
  } catch (error) {
    console.error('❌ [PreLoginState Debug] Error:', error);
    return {
      hasState: false,
      state: null,
      rawStorage: null,
      timestamp: null,
      age: null,
      isValid: false,
    };
  }
}

/**
 * Save chat state before login
 */
export function saveChatState(
  conversationId: string | null,
  sessionId: string | null,
  activeTab?: string
): void {
  savePreLoginState({
    chatState: {
      conversationId,
      sessionId,
      activeTab,
    },
  });
}

/**
 * Save booking state before login
 */
export function saveBookingState(
  experienceType?: string,
  experienceSlug?: string
): void {
  savePreLoginState({
    bookingState: {
      experienceType,
      experienceSlug,
    },
  });
}
